import React, { useEffect } from 'react';

import {
  Datagrid,
  SelectField,
  TextField,
  useRecordContext,
  ReferenceArrayField,
  SingleFieldList,
  ReferenceField,
  useListContext,
  usePermissions,
} from 'react-admin';

import {
  Box,
  SxProps,
} from '@mui/material';

import NoResults from '../../layout/NoResults';

import ColoredScoreField from '../../../customFields/ColoredScoreField';
import { RuleTypes } from '../../../constants/ruleTypes';
import TeamChip from '../../../customFields/ColoredChipField';
import { Rule } from '../../../utilities/schemas/rules';
import mapRuleParamsShortSummary from '../ruleFeatures/mapRuleParamsShortSummary';
import hasAccess from '../../../utilities/hasAccess';
import actions, { Actions } from '../../../constants/actions';

const Fields = ({ label }: {label: string}) => {
  const rule = useRecordContext<Rule>();

  if (!rule) return null;

  return (
    <Box key={label}>
      {React.createElement(mapRuleParamsShortSummary[rule.name], { rule })}
    </Box>
  );
};

const perPageOptions = [
  5,
  10,
  15,
  30,
  50,
];

const RuleDatagrid = ({
  children,
  expand,
  isRowExpandable,
  sx,
  idFieldSource,
  idFieldLabel,
}: {
  children: React.ReactElement | false;
  ruleType: RuleTypes;
  expand?: React.ReactElement;
  isRowExpandable?: (record: Rule) => boolean;
  sx?: SxProps;
  idFieldSource?: 'id' | 'originationChainId' | 'name';
  idFieldLabel?: string | false;
}) => {
  const { perPage, setPerPage } = useListContext();
  const { permissions } = usePermissions<Actions[]>();

  useEffect(() => {
    if (!perPageOptions.includes(perPage)) {
      setPerPage(50);
    }
  }, [perPage, setPerPage]);

  return (
    <Datagrid
      expand={expand}
      isRowExpandable={isRowExpandable}
      empty={<NoResults />}
      bulkActionButtons={false}
      sx={sx}
    >
      <TextField sortable={false} source={idFieldSource ?? 'id'} label={idFieldLabel ?? 'ID'} />
      <TextField sortable={false} source="customName" label="Name" emptyText="-" />
      <TextField sortable={false} source="name" label="Type" />
      {hasAccess(permissions, actions.PROCESS_LIST) && (
        <ReferenceField sortable={false} reference="processes" source="process">
          <TextField source="name" />
        </ReferenceField>
      )}
      {hasAccess(permissions, actions.TAG_LIST) && (
        <ReferenceArrayField sortable={false} source="tags" reference="tags" label="Tags" emptyText="-">
          <SingleFieldList placeholder="-">
            <TeamChip />
          </SingleFieldList>
        </ReferenceArrayField>
      )}
      <SelectField sortable={false} source="createPing" choices={[{ id: true, name: 'Yes' }, { id: false, name: 'No' }]} />
      <Fields label="Rule parameters" />
      <ColoredScoreField source="score" />
      {children}
    </Datagrid>
  );
};

export default RuleDatagrid;
